import { type ReactNode, useState } from "react";
import { Link, useLocation } from "wouter";
import { clsx } from "clsx";
import { Sun, ClipboardList, ArrowDownToLine, ArrowUpFromLine, Boxes, History, Upload, Settings, LogOut, Menu, ShoppingCart, ArrowLeftRight, Warehouse, WifiOff } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { useAlmacen } from "@/lib/almacen";
import { Logo } from "./Logo";
import { useEnLinea } from "@/lib/useEnLinea";

type Item = { href: string; etiqueta: string; icono: ReactNode; soloAdmin?: boolean; soloManual?: boolean; soloMulti?: boolean };

const ITEMS: Item[] = [
  { href: "/hoy", etiqueta: "Hoy", icono: <Sun className="h-5 w-5" /> },
  { href: "/contar", etiqueta: "Contar", icono: <ClipboardList className="h-5 w-5" /> },
  { href: "/entradas", etiqueta: "Entradas", icono: <ArrowDownToLine className="h-5 w-5" /> },
  { href: "/bajas", etiqueta: "Bajas", icono: <ArrowUpFromLine className="h-5 w-5" /> },
  { href: "/ventas", etiqueta: "Ventas del día", icono: <ShoppingCart className="h-5 w-5" />, soloManual: true },
  { href: "/transferir", etiqueta: "Traspasos", icono: <ArrowLeftRight className="h-5 w-5" />, soloMulti: true },
  { href: "/inventario", etiqueta: "Inventario", icono: <Boxes className="h-5 w-5" /> },
  { href: "/historial", etiqueta: "Historial", icono: <History className="h-5 w-5" /> },
  { href: "/importar", etiqueta: "Importar", icono: <Upload className="h-5 w-5" />, soloAdmin: true },
  { href: "/ajustes", etiqueta: "Ajustes", icono: <Settings className="h-5 w-5" />, soloAdmin: true },
];

export function Shell({ children }: { children: ReactNode }) {
  const { perfil, esAdmin, salir } = useAuth();
  const { almacenes, almacen, setAlmacenId, multi } = useAlmacen();
  const [loc] = useLocation();
  const [menu, setMenu] = useState(false);
  const enLinea = useEnLinea();
  const modoManual = perfil?.empresa.modo_ventas === "manual";

  const visibles = ITEMS.filter((i) => (!i.soloAdmin || esAdmin) && (!i.soloManual || modoManual) && (!i.soloMulti || multi));
  // Barra inferior por rol: el almacenista captura; el dueño mira Hoy.
  const barra = esAdmin
    ? visibles.filter((i) => ["/hoy", "/inventario", "/contar"].includes(i.href))
    : visibles.filter((i) => ["/contar", "/entradas", "/bajas", "/inventario"].includes(i.href));
  const enMas = visibles.filter((i) => !barra.some((b) => b.href === i.href));

  const NavLink = ({ item, compacto }: { item: Item; compacto?: boolean }) => {
    const activo = loc === item.href || loc.startsWith(item.href + "/");
    return (
      <Link
        href={item.href}
        onClick={() => setMenu(false)}
        className={clsx(
          "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
          compacto ? "flex-col gap-1 px-1 py-1.5 text-[11px]" : "",
          activo ? "bg-primary text-white" : "text-gray-300 hover:bg-sidebar-hover hover:text-white",
        )}
        aria-current={activo ? "page" : undefined}
      >
        {item.icono}
        <span>{item.etiqueta}</span>
      </Link>
    );
  };

  return (
    <div className="flex min-h-full">
      {/* Sidebar escritorio */}
      <aside className="hidden w-60 shrink-0 flex-col bg-sidebar px-3 py-4 text-white md:flex">
        <div className="mb-6 flex items-center gap-2 px-2">
          <Logo className="h-9 w-9" />
          <div className="min-w-0">
            <div className="font-bold leading-tight">EYE IA</div>
            <div className="truncate text-xs text-gray-400">{perfil?.empresa.nombre}</div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-1">{visibles.map((i) => <NavLink key={i.href} item={i} />)}</nav>
        <div className="mt-4 border-t border-white/10 pt-3 text-sm">
          <div className="truncate px-2 text-gray-300">{perfil?.usuario.nombre}</div>
          <div className="px-2 text-xs text-gray-500">{perfil?.usuario.rol}</div>
          <button onClick={salir} className="mt-2 flex w-full items-center gap-2 rounded px-2 py-1.5 text-gray-300 hover:bg-sidebar-hover hover:text-white"><LogOut className="h-4 w-4" /> Salir</button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Barra superior */}
        <header className="sticky top-0 z-30 flex h-14 items-center gap-3 border-b border-borde bg-white px-4">
          <div className="flex items-center gap-2 md:hidden"><Logo className="h-8 w-8" /><span className="font-bold">EYE IA</span></div>
          <div className="flex-1" />
          {!enLinea && <span className="flex items-center gap-1 rounded bg-error-suave px-2 py-1 text-xs font-medium text-red-800"><WifiOff className="h-3.5 w-3.5" /> Sin conexión</span>}
          {multi && almacen && !/^\/contar\/.+/.test(loc) && (
            <label className="flex items-center gap-2 text-sm">
              <Warehouse className="h-4 w-4 text-tinta-suave" aria-hidden />
              <span className="sr-only">Almacén</span>
              <select value={almacen.id} onChange={(e) => setAlmacenId(e.target.value)} className="h-9 rounded-md border border-borde bg-white px-2 text-sm font-medium">
                {almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
              </select>
            </label>
          )}
          {almacen && (!multi || /^\/contar\/.+/.test(loc)) && <span className="hidden text-sm text-tinta-suave md:inline">{almacen.nombre}</span>}
          <button onClick={() => setMenu(true)} className="rounded p-2 hover:bg-gray-100 md:hidden" aria-label="Más opciones"><Menu className="h-6 w-6" /></button>
        </header>

        <main className="flex-1 px-4 py-4 pb-24 md:px-8 md:py-6 md:pb-8">{children}</main>

        {/* Barra inferior móvil */}
        <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-white/10 bg-sidebar px-1 pb-[env(safe-area-inset-bottom)] pt-1 md:hidden" aria-label="Navegación">
          {barra.slice(0, 4).map((i) => <NavLink key={i.href} item={i} compacto />)}
        </nav>

        {/* Hoja "Más" móvil */}
        {menu && (
          <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-label="Menú">
            <div className="absolute inset-0 bg-black/50" onClick={() => setMenu(false)} />
            <div className="absolute inset-x-0 bottom-0 rounded-t-xl bg-sidebar p-4 text-white">
              <div className="mb-3 flex items-center justify-between">
                <div><div className="font-semibold">{perfil?.usuario.nombre}</div><div className="text-xs text-gray-400">{perfil?.empresa.nombre}</div></div>
                <button onClick={salir} className="flex items-center gap-1 text-sm text-gray-300"><LogOut className="h-4 w-4" /> Salir</button>
              </div>
              <div className="grid grid-cols-2 gap-1">{enMas.map((i) => <NavLink key={i.href} item={i} />)}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
